/**
 * Gamepad input via the standard mapping (R2 §1.1): left stick / d-pad
 * steer, A (0) jump, RT (7) accelerate, LT (6) brake — triggers treated as
 * DIGITAL via a 0.35 threshold so the input log stays boolean (determinism).
 * Start (9) pause. Steer quantized to 1/100 steps for compact exact replays.
 */
import type { InputFrame } from "./sim";

const DEADZONE = 0.2;
const TRIGGER_THRESHOLD = 0.35;

export class GamepadInput {
  private prevStart = false;
  private prevJump = false;
  private pauseQueued = false;
  private jumpPressQueued = false;

  /** Poll once per render frame (the Gamepad API is poll-based). */
  poll(): void {
    const pad = this.firstPad();
    if (!pad) return;
    const start = pad.buttons[9]?.pressed ?? false;
    if (start && !this.prevStart) this.pauseQueued = true;
    this.prevStart = start;
    const jump = pad.buttons[0]?.pressed ?? false;
    if (jump && !this.prevJump) this.jumpPressQueued = true;
    this.prevJump = jump;
  }

  frame(): InputFrame {
    const pad = this.firstPad();
    if (!pad) return { steer: 0, jumpHeld: false, accel: false, brake: false };
    let steer = pad.axes[0] ?? 0;
    if (Math.abs(steer) < DEADZONE) steer = 0;
    if (pad.buttons[14]?.pressed) steer = -1; // d-pad left
    if (pad.buttons[15]?.pressed) steer = 1; // d-pad right
    steer = Math.round(Math.max(-1, Math.min(1, steer)) * 100) / 100;
    return {
      steer,
      jumpHeld: pad.buttons[0]?.pressed ?? false,
      accel: (pad.buttons[7]?.value ?? 0) >= TRIGGER_THRESHOLD,
      brake: (pad.buttons[6]?.value ?? 0) >= TRIGGER_THRESHOLD,
    };
  }

  consumePause(): boolean {
    const v = this.pauseQueued;
    this.pauseQueued = false;
    return v;
  }

  consumeJumpPress(): boolean {
    const v = this.jumpPressQueued;
    this.jumpPressQueued = false;
    return v;
  }

  private firstPad(): Gamepad | null {
    if (typeof navigator.getGamepads !== "function") return null;
    for (const pad of navigator.getGamepads()) {
      if (pad && pad.connected) return pad;
    }
    return null;
  }
}
